import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../stores/auth.js';
import { ChipIcon } from '../components/ChipIcon.js';
import { Leaderboard } from '../components/Leaderboard.js';
import { Achievements } from '../components/Achievements.js';
import { TopupSheet } from '../components/TopupSheet.js';
import { WithdrawSheet } from '../components/WithdrawSheet.js';
import { ReferralSheet } from '../components/ReferralSheet.js';
import { haptic } from '../lib/telegram.js';

export function Profile() {
  const user = useAuth((s) => s.user);
  const [topupOpen, setTopupOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  if (!user) return <div className="grid min-h-full place-items-center text-ivory/60">…</div>;

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col gap-6 px-5 py-6">
      <div className="flex items-center gap-4">
        <div
          className="h-20 w-20 overflow-hidden rounded-full"
          style={{ border: '1px solid var(--color-gold)' }}
        >
          {user.photoUrl ? (
            <img src={user.photoUrl} alt={user.firstName} className="h-full w-full object-cover" />
          ) : (
            <div className="grid h-full w-full place-items-center bg-felt-deep text-3xl text-gold heading-display">
              {user.firstName.slice(0, 1)}
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <h1 className="heading-display text-3xl">{user.firstName}</h1>
          {user.username && <p className="text-sm text-ivory/60">@{user.username}</p>}
        </div>
      </div>

      <div
        className="flex items-center justify-between rounded-2xl p-4"
        style={{
          background: 'var(--color-glass)',
          border: '1px solid color-mix(in oklab, var(--color-gold) 50%, transparent)',
        }}
      >
        <div className="flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-wider text-ivory/60">Фишки</span>
          <span
            className="flex items-center gap-2 text-3xl text-gold"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {user.starsBalance.toLocaleString('ru-RU')}
            <ChipIcon size={22} />
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={() => {
              haptic('light');
              setTopupOpen(true);
            }}
            className="btn-primary"
          >
            Пополнить
          </button>
          <button
            onClick={() => {
              haptic('light');
              setWithdrawOpen(true);
            }}
            className="btn-ghost text-sm"
          >
            Вывести
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Рейтинг" value={user.rating.toString()} />
        <Stat label="Партий" value={user.totalGames.toString()} />
        <Stat label="Побед" value={user.totalWins.toString()} />
        <Stat
          label="Винрейт"
          value={
            user.totalGames === 0
              ? '—'
              : `${Math.round((user.totalWins / user.totalGames) * 100)}%`
          }
        />
      </div>

      <button
        onClick={() => {
          haptic('light');
          setReferralOpen(true);
        }}
        className="flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-left transition-colors hover:bg-white/[0.04]"
        style={{
          background: 'var(--color-glass)',
          border: '1px solid color-mix(in oklab, var(--color-gold) 35%, transparent)',
        }}
      >
        <div className="flex flex-col">
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 16 }}>
            Пригласить друга
          </span>
          <span className="text-xs text-ivory/55">+50 фишек тебе и другу за первую партию</span>
        </div>
        <span className="text-gold text-xl">→</span>
      </button>

      <Achievements />

      <Leaderboard myId={user.id} />

      <nav className="mt-2 flex items-center justify-center gap-4 text-xs text-ivory/55">
        <Link to="/terms" className="underline-offset-2 hover:underline">
          Условия
        </Link>
        <span aria-hidden="true">·</span>
        <Link to="/privacy" className="underline-offset-2 hover:underline">
          Конфиденциальность
        </Link>
        <span aria-hidden="true">·</span>
        <a
          href="https://t.me/durakapp_game_bot"
          target="_blank"
          rel="noreferrer"
          className="underline-offset-2 hover:underline"
        >
          Поддержка
        </a>
      </nav>

      <TopupSheet open={topupOpen} onClose={() => setTopupOpen(false)} />
      <WithdrawSheet open={withdrawOpen} onClose={() => setWithdrawOpen(false)} />
      <ReferralSheet open={referralOpen} onClose={() => setReferralOpen(false)} />
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  full,
}: {
  label: string;
  value: string;
  accent?: boolean;
  full?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl p-4 ${full ? 'col-span-2' : ''}`}
      style={{
        background: 'var(--color-glass)',
        border: '1px solid color-mix(in oklab, var(--color-gold) 30%, transparent)',
      }}
    >
      <p className="text-xs uppercase tracking-wider text-ivory/60">{label}</p>
      <p className={`mt-1 flex items-center gap-2 text-2xl ${accent ? 'text-gold' : ''}`} style={{ fontFamily: 'var(--font-display)' }}>
        <span>{value}</span>
        {accent && <ChipIcon size={20} />}
      </p>
    </div>
  );
}
